import { useEffect } from "react";
import { createPortal } from "react-dom";
import { errorMessage } from "../api/client";
import { usePaymentStatement } from "../api/payments";
import type { PaymentUserRow } from "../api/types";
import { formatBalance, formatDateTime, initials, methodLabel, userLabel } from "../lib/payments";
import { maskPerson } from "../lib/mask";

interface Props {
  uid: string | null; // null = closed
  onClose: () => void;
  onRegister: (user: PaymentUserRow) => void;
  masked?: boolean; // demo mode: show the person's identity masked
}

/** Right-hand slide-over: one user's balance and every payment registered for them. */
export default function PaymentStatementDrawer({ uid, onClose, onRegister, masked = false }: Props) {
  const open = uid != null;
  const stmt = usePaymentStatement(uid);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const user = stmt.data?.user && (masked ? maskPerson(stmt.data.user) : stmt.data.user);

  return createPortal(
    <>
      <div className={open ? "drawer-scrim open" : "drawer-scrim"} onClick={onClose} />
      <aside className={open ? "drawer open" : "drawer"} aria-hidden={!open} aria-label="Payment statement">
        <div className="drawer-head">
          <div className="pay-avatar large">{user ? initials(user) : "…"}</div>
          <div className="drawer-head-text">
            <h2>{user ? userLabel(user) : "Statement"}</h2>
            <div className="muted small">
              {user ? user.email ?? user.uid : uid}
              {user && !user.is_active && <span className="bal-pill gray">deleted account</span>}
            </div>
          </div>
          <button className="drawer-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="drawer-body">
          {stmt.isPending && open && <p className="muted small">Loading…</p>}
          {stmt.isError && <p className="error">Failed to load: {errorMessage(stmt.error)}</p>}
          {stmt.data && user && (
            <>
              <div className="pay-totals">
                <div>
                  <div className="kpi-label">Approved</div>
                  <div className="pay-total-value">{formatBalance(user.approved_ms)}</div>
                  <div className="small muted">{user.approved_count} recordings</div>
                </div>
                <div>
                  <div className="kpi-label">Paid</div>
                  <div className="pay-total-value green">{user.paid_hours}h</div>
                  <div className="small muted">
                    {user.payment_count} payment{user.payment_count === 1 ? "" : "s"}
                  </div>
                </div>
                <div>
                  <div className="kpi-label">Pending</div>
                  <div className={`pay-total-value ${user.pending_ms < 0 ? "red" : "amber"}`}>
                    {user.pending_ms < 0 ? `overpaid ${formatBalance(-user.pending_ms)}` : formatBalance(user.pending_ms)}
                  </div>
                  <div className="small muted">{user.payable_hours}h payable now</div>
                </div>
              </div>

              <p className="pay-section-title">
                <span>Payment statement</span>
                <span>{stmt.data.payments.length} payment{stmt.data.payments.length === 1 ? "" : "s"}</span>
              </p>
              {stmt.data.payments.length === 0 ? (
                <div className="empty-state small">No payments registered for this user yet.</div>
              ) : (
                <ul className="ledger">
                  {stmt.data.payments.map((p) => (
                    <li key={p.id}>
                      <div>
                        <div className="ledger-when">{formatDateTime(p.paid_at)}</div>
                        <div className="small muted">
                          Registered by {p.registered_by}
                          {p.note ? ` · ${p.note}` : ""}
                        </div>
                      </div>
                      <div className="ledger-amt">
                        <div className="green">
                          {methodLabel(p.method) && <span className="pay-method">{methodLabel(p.method)}</span>}
                          + {p.hours}h paid
                        </div>
                        <div className="small muted">
                          total {p.cumulative_hours}h · pending after{" "}
                          {p.pending_ms_after < 0 ? `overpaid ${formatBalance(-p.pending_ms_after)}` : formatBalance(p.pending_ms_after)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>

        <div className="drawer-foot">
          <button className="btn" onClick={onClose}>
            Close
          </button>
          <button
            className="btn primary"
            disabled={!user || user.payable_hours < 1}
            onClick={() => user && onRegister(user)}
          >
            Register payment
          </button>
        </div>
      </aside>
    </>,
    document.body,
  );
}
